import mqtt from "mqtt";
import { addRaiseMsgNotification } from "./notification.service";
import { applyContactUsMessage } from "./contactUsRealtime.service";
import userService from "./user.service";

function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

class MqttService {
  constructor() {
    this.client = null;
    this.isStarted = false;
  }

  isEnabled() {
    // Vue env'leri build-time'dır; runtime'da hızlı test için localStorage override destekle.
    // Örn: localStorage.setItem("ptduo:mqttEnabled","true") + reload
    try {
      const override = localStorage.getItem("ptduo:mqttEnabled");
      if (override !== null) {
        return String(override).trim().toLowerCase() === "true";
      }
    } catch {
      // ignore
    }
    return (
      String(process.env.VUE_APP_MQTT_ENABLED || "")
        .trim()
        .toLowerCase() === "true"
    );
  }

  // Dinamik kimlik bilgilerini yerel depolamadan al
  getCredentials() {
    try {
      const userRaw = localStorage.getItem("user");
      const user = userRaw ? JSON.parse(userRaw) : null;
      // Backend: "password'e accessToken yaz"
      const token = localStorage.getItem("jwt") || localStorage.getItem("token"); 

      return {
        // Backend: "username'e userId yaz"
        username: user?.userId || "", 
        password: token || "",
        userId: user?.userId || ""
      };
    } catch (e) {
      console.error("[MQTT] Kimlik bilgileri okunamadı", e);
      return { username: "", password: "", userId: "" };
    }
  }

  getConfig() {
    // Browser için MQTT bağlantısı sadece WebSocket (ws/wss) ile olur.
    // URL mutlaka .env üzerinden verilmelidir (  wss://host/mqtt).
    const url = process.env.VUE_APP_MQTT_URL;
    const appSegment = process.env.VUE_APP_MQTT_APP || "trainer";

    return { url, appSegment };
  }

  async start() {
    // 1. Çevresel Değişken Kontrolü
    if (!this.isEnabled()) {
      console.warn("[MQTT] Devre dışı (VUE_APP_MQTT_ENABLED true değil / localStorage override kapalı).");
      return;
    }

    // 2. Mükerrer Bağlantı Kontrolü
    if (this.isStarted && this.client) {
      if (this.client.connected) return;
      console.info("[MQTT] Client zaten oluşturulmuş; reconnect bekleniyor.");
      return;
    }

    const { url, appSegment } = this.getConfig();
    const { username, password, userId } = this.getCredentials();

    // 3. Kimlik ve URL Doğrulama
    if (!url) {
      console.warn("[MQTT] VUE_APP_MQTT_URL tanımlı değil. MQTT başlatılmadı.");
      return;
    }

    const urlTrimmed = String(url).trim();
    if (!/^wss?:\/\//i.test(urlTrimmed)) {
      console.warn(
        `[MQTT] VUE_APP_MQTT_URL WebSocket olmalı (ws:// veya wss://). Mevcut: ${urlTrimmed}`
      );
      return;
    }

    // Mixed content ipucu (https sayfada ws:// engellenir)
    try {
      if (window?.location?.protocol === "https:" && /^ws:\/\//i.test(urlTrimmed)) {
        console.warn("[MQTT] Sayfa https ama MQTT URL ws://. Tarayıcı mixed-content nedeniyle engelleyebilir; wss:// kullanın.");
      }
    } catch {
      // ignore
    }

    if (!userId || !password) {
      console.warn("[MQTT] Oturum bilgileri (userId/token) eksik. Bağlantı için login şart.");
      return;
    }

    try {
      // 4. KRİTİK ADIM: Önce yetki kaydını tamamla ve bekle (Emirhan Bey'in belirttiği kural)
      console.log("[MQTT] SaveBrokerAuth yetki kaydı başlatılıyor...");
      await userService.saveBrokerAuth(); 
      console.log("[MQTT] Yetki kaydı başarılı. Broker bağlantısı kuruluyor...");

      this.isStarted = true;
      const clientId = `ptduo-web-${appSegment}-${userId}-${Math.random().toString(16).slice(2, 6)}`;

      // 5. Bağlantı Seçenekleri
      const options = {
        username, // userId
        password, // accessToken
        clientId,
        clean: true,
        resubscribe: true,
        reconnectPeriod: 5000,
        connectTimeout: 30000,
        keepalive: 60,
      };

      // 6. Bağlantıyı Başlat
      console.info(`[MQTT] Bağlanılıyor: ${urlTrimmed} (app=${appSegment}, userId=${userId})`);
      this.client = mqtt.connect(urlTrimmed, options);

      // Topic Tanımları
      const raiseMsgTopic = `ptduo/${appSegment}/user/fe/${userId}/RaiseMsg`;
      const contactUsTopic = `ptduo/${appSegment}/user/fe/${userId}/ContactUs/+`;
      const deleteDeviceTopic = `ptduo/${appSegment}/user/fe/${userId}/DeleteUserDeviceMsg`;

      this.client.on("connect", () => {
        console.info("✅ [MQTT] Broker bağlantısı başarılı.");
        
        this.client.subscribe([raiseMsgTopic, contactUsTopic, deleteDeviceTopic], { qos: 1 }, (err) => {
          if (!err) {
            console.log("[MQTT] Kanallara abone olundu.");
          } else {
            console.error("[MQTT] Abonelik hatası:", err);
          }
        });
      });

      // 7. Mesaj Dinleyici
      this.client.on("message", (topic, message) => {
        const payload = safeJsonParse(message.toString());
        if (!payload) return;

        console.log(`[MQTT] Yeni Mesaj (${topic}):`, payload);

        const parts = topic.split("/");
        const feature = parts[5]; 

        if (feature === "RaiseMsg") {
          addRaiseMsgNotification(payload, topic);
        } else if (feature === "ContactUs") {
          applyContactUsMessage(payload, topic);
        } else if (feature === "DeleteUserDeviceMsg") {
          if (payload.userId === userId) {
            console.warn("[MQTT] Bu cihazın yetkisi iptal edildi.");
            window.dispatchEvent(new CustomEvent("ptduo:logout"));
          }
        }
      });

      // 8. Olay Yönetimi (Error, Reconnect vb.)
      this.client.on("error", (err) => {
        const details = {
          message: err?.message,
          code: err?.code,
          name: err?.name,
          stack: err?.stack
        };
        console.error("❌ [MQTT] Hata:", details);
      });

      this.client.on("offline", () => {
        if (this.isStarted) console.warn("⚠️ [MQTT] Offline (network/broker erişilemedi).");
      });

      this.client.on("reconnect", () => {
        if (this.isStarted) console.warn("⚠️ [MQTT] Yeniden bağlanılıyor...");
      });

      this.client.on("close", () => {
        if (this.isStarted) console.warn("⚠️ [MQTT] Bağlantı kapandı.");
      });

    } catch (err) {
      // SaveBrokerAuth hatası alınırsa bağlantıyı hiç başlatma
      console.error("❌ [MQTT] Yetki hatası (SaveBrokerAuth) nedeniyle durduruldu:", err?.message || err);
      this.isStarted = false;
    }
  }

  stop() {
    this.isStarted = false;
    if (this.client) {
      this.client.end(true, () => {
        console.log("[MQTT] Bağlantı güvenli şekilde kapatıldı.");
      });
      this.client = null;
    }
  }
}

export default new MqttService();
